/**
 * Module-level pub/sub for dashboard action lifecycle events.
 *
 * Mirrors `vercel-labs/json-render`'s action-observer pattern. Future
 * actions catalog (§1.6 — see IMPROVEMENTS_FROM_JSON_RENDER.md) will
 * call `notifyActionDispatch` / `notifyActionSettle` around every
 * dispatched handler. Devtools (§1.1) and any telemetry consumer
 * subscribe via `registerActionObserver`.
 *
 * Additive and non-breaking: nothing here runs unless a consumer
 * registers an observer AND a future caller dispatches actions. This
 * module ships the observer surface so §1.6 has somewhere to call into.
 */

let logger: { error: (...args: unknown[]) => void } | null = null

/**
 * Resolve the active logger, falling back to `console` so observer
 * throws aren't silently swallowed in production. Tests inject a
 * silent stub via `_setObserverLogger` to keep their output clean.
 */
function activeLogger(): { error: (...args: unknown[]) => void } {
  return logger ?? console
}

/** Test-only: inject a logger (or null to fall back to console). */

export function _setObserverLogger(next: typeof logger): void {
  logger = next
}

/**
 * Emitted when an action begins executing. The same `id` will appear
 * on a matching {@link ActionSettleInfo} when the action resolves or throws.
 */
export interface ActionDispatchInfo {
  /** Stable dispatch id; paired with the matching `onSettle`. */
  id: string
  /** Action name from the catalog (e.g. "drillTo", "exportCsv"). */
  name: string
  /** Resolved params, if any. */
  params?: Record<string, unknown>
  /** Wall-clock time (ms since epoch) at dispatch. */
  at: number
}

/** Emitted after an action handler resolves or throws. */
export interface ActionSettleInfo {
  /** Matches the `id` of the corresponding {@link ActionDispatchInfo}. */
  id: string
  /** Action name. */
  name: string
  /** `true` if the handler resolved, `false` if it threw. */
  ok: boolean
  /** Wall-clock time (ms since epoch) at settle. */
  at: number
  /** Elapsed time in milliseconds. */
  durationMs: number
  /** Return value from the handler, if any. */
  result?: unknown
  /** Error if the handler threw. */
  error?: unknown
}

/** Observer for action lifecycle events. Either callback is optional. */
export interface ActionObserver {
  onDispatch?: (evt: ActionDispatchInfo) => void
  onSettle?: (evt: ActionSettleInfo) => void
}

const observers = new Set<ActionObserver>()

/**
 * Register an observer. Returns an unsubscribe function. Safe to call
 * from any layer — devtools, analytics, audit logging, etc.
 *
 * @example
 * ```ts
 * const unsub = registerActionObserver({
 *   onDispatch: (evt) => console.log("fired", evt.name),
 *   onSettle: (evt) => console.log("settled", evt.name, evt.durationMs),
 * })
 * // later: unsub()
 * ```
 */
export function registerActionObserver(observer: ActionObserver): () => void {
  observers.add(observer)
  return () => {
    observers.delete(observer)
  }
}

/** Number of currently-registered observers. Test/devtools helper. */

export function _observerCount(): number {
  return observers.size
}

/** Test-only: clear all observers. Avoids cross-test bleed. */

export function _resetObservers(): void {
  observers.clear()
}

/**
 * Emit a dispatch event to all registered observers. Wrapped in
 * try/catch per-observer so a buggy observer cannot interrupt action
 * execution.
 */
export function notifyActionDispatch(evt: ActionDispatchInfo): void {
  for (const o of observers) {
    const fn = o.onDispatch
    if (!fn) continue
    try {
      fn(evt)
    } catch (err) {
      activeLogger().error("[dash] action observer threw in onDispatch:", err)
    }
  }
}

/** Emit a settle event to all registered observers. */
export function notifyActionSettle(evt: ActionSettleInfo): void {
  for (const o of observers) {
    const fn = o.onSettle
    if (!fn) continue
    try {
      fn(evt)
    } catch (err) {
      activeLogger().error("[dash] action observer threw in onSettle:", err)
    }
  }
}

/**
 * Convenience wrapper for action handlers. Generates a stable id,
 * fires onDispatch immediately, runs the handler, then fires onSettle
 * with timing + outcome. Re-throws so callers see the original error.
 *
 * Future actions-catalog dispatcher (§1.6) will use this around every
 * registered handler.
 */
export async function withActionObservers<T>(
  name: string,
  params: Record<string, unknown> | undefined,
  handler: () => T | Promise<T>
): Promise<T> {
  const id = `${name}:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`
  const start = Date.now()
  notifyActionDispatch({ id, name, params, at: start })
  try {
    const result = await handler()
    notifyActionSettle({
      id,
      name,
      ok: true,
      at: Date.now(),
      durationMs: Date.now() - start,
      result,
    })
    return result
  } catch (err) {
    notifyActionSettle({
      id,
      name,
      ok: false,
      at: Date.now(),
      durationMs: Date.now() - start,
      error: err,
    })
    throw err
  }
}
